/* eslint-disable import/no-cycle */
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  Signal,
  Type,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { Subject, debounceTime, distinctUntilChanged, filter, map, skip } from 'rxjs'

import { FlxBreadcrumbsStyleDirective, FlxThemeModule } from '@flowx/angular-theme'
import {
  FlxBreadcrumbsComponent,
  FlxIconModule,
  FlxSpinnerComponent,
  FlxTemplateDirective,
  FlxToastService,
  ToastData,
} from '@flowx/angular-ui-toolkit'
import {
  ComponentIdentifierEnum,
  FooterTemplateConfig,
  HeaderTemplateConfig,
  ModalTemplateConfig,
  TemplateConfig,
  UiFlowSetupOptions,
  addComponentsToRegistry,
  addToNavigationStack,
  addValidatorsToRegistry,
  closeModal,
  config,
  getOpenModal,
  getTemplateConfigByFlowxUuid,
  getUiFlowRestoredProcessEmbedded,
  getUrlQueryParamValue,
  initUiFlow,
  navigationStack,
  openModal,
  openModal$,
  resetChatStores,
  resetNavigationStack,
  resetUiFlowStores,
  resolveRootNavigationQueryParams,
  setAccessToken,
  snapshotAndClearNavigationStack,
  toasts$,
  triggerDisplayEvent,
  uiFlowBlockedReason$,
  uiFlowGlobalTemplateConfigsForCurrentRoot$,
  uiFlowProcessStore,
  uiFlowRestoredProcessEmbedded$,
  uiFlowTemplateConfigs$,
  uiFlowTemplates$,
  uiStore$,
  updateUrlWithQueryParam,
  updateUrlWithQueryParams,
  urlUpdate$,
  viewNavigationStack$,
} from '@flowx/core-sdk'
import { theme } from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

import { FlxUiFlowAccessDeniedComponent } from './ui-flow-access-denied.component'

import { FlxChatWrapperComponent } from '../components/chat-wrapper/chat-wrapper.component'
import { FlxFooterComponent } from '../components/flx-footer/flx-footer.component'
import { FlxHeaderComponent } from '../components/flx-header/flx-header.component'
import { getDynamicPropSignal, getThemeIcon } from '../components/helpers'
import { FlxModalWrapperComponent } from '../components/modal-wrapper/modal-wrapper.component'
import { FlxProcessRendererWrapperComponent } from '../components/process-renderer-wrapper/process-renderer-wrapper.component'
import { FlxLocalizePipe } from '../pipes'
import { FlxProcessRendererComponent } from '../process-renderer/process-renderer.component'
import { FlxTreeNodeComponent } from '../renderer/tree-node/tree-node.component'
import { stableProjection } from '../utils/stable-projection.utils'
import { FLX_VALIDATORS, NgAsyncValidator, NgSyncValidator } from '../utils/validators'

@Component({
  selector: 'flx-ui-flow',
  imports: [
    FlxTreeNodeComponent,
    FlxProcessRendererComponent,
    FlxBreadcrumbsComponent,
    FlxThemeModule,
    FlxBreadcrumbsStyleDirective,
    FlxIconModule,
    FlxTemplateDirective,
    FlxLocalizePipe,
    FlxSpinnerComponent,
    FlxModalWrapperComponent,
    FlxChatWrapperComponent,
    FlxHeaderComponent,
    FlxFooterComponent,
    FlxUiFlowAccessDeniedComponent,
  ],
  template: `
    @if (!showProcess()) {
      <!-- LOADER -->
      @if (loaderState$().showLoader !== undefined) {
        <flx-spinner
          [text]="loaderState$().loaderText | flxLocalize"
          type="indeterminate"
          visualType="custom"
          size="small"
          backgroundColor="var(--flxTheme-spinner-backgroundColor, #ffffff)"
          borderColor="var(--flxTheme-spinner-accentColor, transparent)"
          flxSpinner
        />
      }
      @if (blockedReason()) {
        <flx-ui-flow-access-denied [reason]="blockedReason()" />
      } @else {
        @if (headerTemplateConfig()) {
          <flx-header [templateConfig]="headerTemplateConfig()" />
        }
        @if (breadcrumbsTemplateConfig()) {
          <flx-breadcrumbs
            [componentStyle]="{
              style: breadcrumbsTemplateConfig().style,
            }"
            [items]="navigationStackItems()"
            [maxVisibleItems]="breadcrumbsTemplateConfig().maxItems"
            [ariaLabel]="breadcrumbsAriaLabel | flxLocalize"
          >
            <ng-template
              flxTemplate="item"
              let-item
              let-index="index"
            >
              {{ $safeNavigationMigration(navigationStackItems()[index]?.label()) | flxLocalize }}
            </ng-template>
            @if (breadcrumbsTemplateConfig().showHomeIcon) {
              <flx-icon
                [path]="icons().home"
                flxBreadcrumbHome
              />
            }
            <ng-template flxTemplate="separator">
              <flx-icon [path]="icons().separator" />
            </ng-template>
          </flx-breadcrumbs>
        }
        @if (globalChatConfig()) {
          <flx-chat-wrapper [templateConfig]="globalChatConfig()" />
        }
        @if (screenTemplateConfig()) {
          <flx-tree-node
            #treeNode
            [templateConfig]="screenTemplateConfig()"
          />
        }
        @if (openModalConfig()) {
          <flx-modal-wrapper [templateConfig]="openModalConfig()" />
        }
        @if (footerTemplateConfig()) {
          <flx-footer [templateConfig]="footerTemplateConfig()" />
        }
      }
    } @else {
      <flx-process-renderer
        [apiUrl]="apiUrl()"
        [authToken]="authToken()"
        [resourceId]="$safeNavigationMigration(processData()?.processResourceId)"
        [staticAssetsPath]="staticAssetsPath()"
        [themeId]="themeId()"
        [language]="language()"
        [locale]="locale()"
        [cache]="cache()"
        [processApiPath]="processApiPath()"
        [processStartData]="$safeNavigationMigration(processData()?.processStartData)"
        [projectInfo]="resolvedProjectInfo()"
        [workspaceId]="workspaceId()"
        [organizationId]="organizationId()"
        trigger="action"
      />
    }
  `,
  styles: `
    :host {
      flex: initial;
    }
    :host(.flx-ui-flow-fill) {
      display: flex;
      flex-direction: column;
      flex: 1;
    }
    .breadcrumb {
      display: flex;
      flex-direction: row;
      gap: 0.5rem;
      list-style: none;
      padding: 0;
      :not(:last-child) {
        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  `,
  host: {
    '[class.flx-ui-flow-fill]': 'hasFixedFooter()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxUiFlowRendererComponent implements OnInit, OnDestroy {
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #activatedRoute = inject(ActivatedRoute)
  #destroyRef = inject(DestroyRef)
  #injector = inject(Injector)
  #toastService = inject(FlxToastService)

  authToken = input<string | null>()
  apiUrl = input.required<string>()
  staticAssetsPath = input.required<string>()

  workspaceId = input.required<string>()
  projectId = input.required<string>()
  organizationId = input.required<string>()
  buildId = input<string>(null)
  resourceId = input<string>(null)
  resourceDefinitionId = input<string>(null)
  appVersionId = input<string>(null)
  uiFlowName = input.required<string>()
  themeId = input.required<string>()

  language = input.required<string>()
  locale = input.required<string>()

  isDraft = input<boolean>(false)
  cache = input<boolean>(true)

  customComponents = input<Record<string, Type<any>>>(null)
  customValidators = input<Record<string, NgSyncValidator | NgAsyncValidator>>(null)

  // process renderer inputs
  processApiPath = input<string>(null)
  startParams = input<Record<string, unknown>>({})

  blockedReason = toSignal(uiFlowBlockedReason$, { initialValue: null })
  screenTemplateConfig = toSignal(uiFlowTemplates$)
  globalTemplateConfigs = toSignal(uiFlowGlobalTemplateConfigsForCurrentRoot$, { initialValue: [] })
  loaderState$ = toSignal(
    uiStore$.pipe(stableProjection(({ showLoader, loaderText }) => ({ showLoader, loaderText }))),
    {
      initialValue: { showLoader: undefined, loaderText: undefined },
    }
  )

  breadcrumbsTemplateConfig = computed(() => {
    const config = this.globalTemplateConfigs()?.find(
      (config) => config.componentIdentifier === ComponentIdentifierEnum.BREADCRUMBS
    ) as any
    if (!config) {
      return null
    }
    return {
      showHomeIcon: config.displayOptions.flowxProps.showHomeIcon,
      maxItems:
        config.displayOptions.flowxProps.collapseMode === 'NONE'
          ? null
          : config.displayOptions.flowxProps.maxVisibleItems,
      style: config.displayOptions.style,
    }
  })

  globalChatConfig = computed(() => {
    const config = this.globalTemplateConfigs()?.find(
      (config) => config.componentIdentifier === ComponentIdentifierEnum.CHAT
    ) as any

    if (!config) {
      return null
    }
    return config
  })

  headerTemplateConfig = computed(
    () =>
      (this.globalTemplateConfigs()?.find(
        (config) => config.componentIdentifier === ComponentIdentifierEnum.HEADER
      ) as HeaderTemplateConfig | undefined) ?? null
  )

  footerTemplateConfig = computed(
    () =>
      (this.globalTemplateConfigs()?.find(
        (config) => config.componentIdentifier === ComponentIdentifierEnum.FOOTER
      ) as FooterTemplateConfig | undefined) ?? null
  )

  hasFixedFooter = computed(() => {
    const footer = this.footerTemplateConfig()
    return !!footer && footer.displayOptions?.style?.position?.positionType === 'fixed'
  })

  icons = computed(() => ({
    home: getThemeIcon(ThemeComponent.Breadcrumbs, 'homeIcon'),
    separator: getThemeIcon(ThemeComponent.Breadcrumbs, 'separatorIcon'),
  }))
  breadcrumbsAriaLabel = '@@sys_a11y_breadcrumb'

  processData = signal<{
    processResourceId?: string
    processStartData?: Record<string, unknown>
    appId?: string
    buildId?: string
  } | null>(null)
  showProcess = signal<boolean>(false)

  treeNode = viewChild('treeNode', { read: ElementRef })
  navigationStackItems: Signal<{ label: Signal<string>; onClick: () => void }[]>
  FlxProcessRendererWrapperComponent = FlxProcessRendererWrapperComponent
  fullProcessClosed$ = new Subject<void>()

  openModalFlowxUuid = toSignal(openModal$)
  // Track template config changes so the computed re-evaluates when configs are loaded
  // (e.g., on page refresh where openModal fires before initUiFlow loads configs).
  templateConfigs = toSignal(uiFlowTemplateConfigs$, {
    initialValue: [] as TemplateConfig[],
  })
  openModalConfig = computed(() => {
    const flowxUuid = this.openModalFlowxUuid()
    // Read templateConfigs to establish a reactive dependency — the computed
    // re-evaluates when template configs are loaded, not just when the modal ID changes.
    this.templateConfigs()
    return flowxUuid ? (getTemplateConfigByFlowxUuid(flowxUuid) as ModalTemplateConfig) : null
  })

  resolvedProjectInfo = computed(() => {
    const appConfig = config<UiFlowSetupOptions>()
    return {
      projectId: this.processData()?.appId || appConfig?.appInfo?.appId || this.projectId(),
      appVersionId: this.processData()?.buildId || appConfig?.buildId || this.buildId(),
    }
  })

  constructor() {
    effect(() => {
      const authToken = this.authToken()
      if (!authToken) {
        return
      }
      // Locked: a renderer initialized in anonymous mode never flips to authed.
      if (config<UiFlowSetupOptions>()?.isAnonymousMode) {
        return
      }
      // Updates the global config and re-authenticates live SSE connections.
      setAccessToken(authToken)
    })

    effect((onCleanup) => {
      let observer: MutationObserver
      if (this.treeNode()) {
        observer = new MutationObserver(() => {
          setTimeout(() => {
            const viewId = getUrlQueryParamValue('viewId')
            const scrollElement = document.getElementById(viewId)
            if (scrollElement) {
              scrollElement.scrollIntoView({ behavior: 'smooth' })
            }
          })
        })

        observer.observe(this.treeNode().nativeElement, {
          childList: true,
          subtree: false,
        })
        onCleanup(() => {
          observer.disconnect()
        })
        setTimeout(() => {
          const viewId = getUrlQueryParamValue('viewId')
          const scrollElement = document.getElementById(viewId)
          if (scrollElement) {
            scrollElement.scrollIntoView({ behavior: 'smooth' })
          }
        })
      }
    })
  }

  async ngOnInit(): Promise<void> {
    // Snapshot sessionStorage and clear navigation stack atomically.
    // The snapshot must be taken before clear() removes the sessionStorage entry.
    const snapshot = snapshotAndClearNavigationStack()

    // Detect full-screen process: snapshot must have processUuid (only set by
    // loadProcess's push) AND the URL must still have processUuid (cleared by
    // the popstate handler when the process is properly dismissed).
    const processUuidInUrl = getUrlQueryParamValue('processUuid')
    const hadFullScreenProcess = !!snapshot?.ephemeral?.processUuid && !!processUuidInUrl

    // Immediately seed the navigation stack for refresh recovery.
    // Must happen before initUiFlow (async) so the popstate handler can detect
    // the entry if the user presses back before initialization completes.
    if (snapshot?.ephemeral?.modalId) {
      navigationStack.seed(snapshot.route, snapshot.ephemeral)
    } else if (hadFullScreenProcess) {
      navigationStack.seed(snapshot.route, snapshot.ephemeral)
    }

    addValidatorsToRegistry<NgSyncValidator>({
      ...FLX_VALIDATORS,
      ...this.customValidators(),
    })
    if (this.customComponents()) {
      addComponentsToRegistry<Type<any>>(this.customComponents())
    }

    // TEMPORARY: add the process renderer wrapper component to the registry
    addComponentsToRegistry<Type<any>>({
      [ComponentIdentifierEnum.PROCESS_RENDERER]: FlxProcessRendererWrapperComponent,
    })

    // this is necessary because the renderer does not update the query param through angular, so angular cannot detect the change
    urlUpdate$
      .pipe(
        map((url: URL) => {
          const params: Record<string, string> = {}
          url.searchParams.forEach((value, key) => {
            params[key] = value
          })
          return params
        }),
        debounceTime(0),
        filter((params) => !!params['uiFlowSessionUuid']),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((queryParams) => {
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams,
          replaceUrl: true,
        })
      })

    runInInjectionContext(this.#injector, () => {
      this.navigationStackItems = toSignal(
        viewNavigationStack$.pipe(
          map((items) =>
            items.map((item) => {
              const title =
                'displayOptions' in item && 'title' in item.displayOptions.flowxProps
                  ? item.displayOptions.flowxProps.title || ''
                  : ''
              return {
                label: runInInjectionContext(this.#injector, () =>
                  getDynamicPropSignal(title, item)
                ),
                onClick: () => this.navigateToTemplateConfig({ flowxUuid: item.flowxUuid }),
              }
            })
          )
        ),
        { initialValue: [] }
      )
    })

    this.#activatedRoute.queryParams
      .pipe(
        map((params) => params.viewId),
        skip(1),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((viewId) => {
        updateUrlWithQueryParam('viewId', viewId, false, true)

        const scrollElement = document.getElementById(viewId)
        if (scrollElement) {
          scrollElement.scrollIntoView({ behavior: 'smooth' })
        }

        // Don't trigger display events when a modal is opening — the viewId change
        // is a side effect of modal-open navigation, not a real page navigation.
        // Display events for the page will fire via 'modal-dismiss' when the modal closes.
        if (!getOpenModal()) {
          triggerDisplayEvent('navigation', viewId)
        }
      })

    this.#activatedRoute.queryParams
      .pipe(
        map((params) => params['modalId']),
        skip(1),
        distinctUntilChanged(),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((modalId) => {
        if (modalId) {
          // modalId appeared in URL (e.g., browser forward) — re-open the modal
          openModal(modalId, false)
        } else {
          closeModal()
        }
      })

    uiFlowProcessStore.stream$
      .pipe(
        filter((process) => !!process?.processResourceId),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((process) => {
        if (process?.processResourceId) {
          this.loadProcess(
            process.processResourceId,
            process.processStartData ?? {},
            process.appId,
            process.buildId
          )
        }
      })

    // Reactive rescue for deep-link / fresh-tab opens where the synchronous
    // `hadFullScreenProcess` gate at the end of ngOnInit was false (empty
    // sessionStorage). When initUiFlow later sets the embedded flag to false
    // and a processUuid is still in the URL, mount the fullscreen renderer.
    // Inverse case (embedded === true after we already flipped on) restores
    // the in-tree wrapper, preserving the original `uiFlowEmbedsRunningProcess$`
    // intent from commit d26362863a.
    uiFlowRestoredProcessEmbedded$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((embedded) => {
        const processUuidStillInUrl = getUrlQueryParamValue('processUuid')
        if (embedded === false && processUuidStillInUrl && !this.showProcess()) {
          this.processData.set({ processResourceId: '', processStartData: {} })
          this.showProcess.set(true)
        } else if (embedded === true && this.showProcess()) {
          this.showProcess.set(false)
          this.processData.set(null)
        }
      })

    navigationStack.navigation$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((event) => {
      if (event.type === 'pop' || event.type === 'reset') {
        const poppedEphemeral = event.previousEntry?.ephemeral

        // If showing a full-screen process and the popped entry was NOT a modal
        // (i.e., we're not just closing a modal within the process), hide the process.
        if (this.showProcess() && !poppedEphemeral?.modalId) {
          updateUrlWithQueryParam('processUuid', null)
          this.showProcess.set(false)
          this.processData.set(null)
          this.fullProcessClosed$.next()
        }

        // Sync breadcrumb trail when navigating back to a view
        const destinationViewId = event.entry?.route?.viewId
        if (destinationViewId && !this.showProcess()) {
          const tc = getTemplateConfigByFlowxUuid(destinationViewId)
          addToNavigationStack(tc ?? null)
        }
      }
    })

    // show toasts
    toasts$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((toast) => {
      const toastData: ToastData = { ...toast }
      if (toast.withTheme) {
        toastData.className = getComponentStyles(ThemeComponent.Message, theme()) || ''
      }
      this.#toastService.show(toastData)
    })

    await initUiFlow({
      authToken: this.authToken(),
      apiUrl: this.apiUrl(),
      staticAssetsPath: this.staticAssetsPath(),
      workspaceId: this.workspaceId(),
      organizationId: this.organizationId(),
      appInfo: {
        appId: this.projectId(),
        appVersionId: this.appVersionId(),
        selectedProcessResourceId: this.resourceId(),
      },
      buildId: this.buildId(),
      uiFlowName: this.uiFlowName(),
      themeId: this.themeId(),
      language: this.language(),
      locale: this.locale(),
      isDraft: this.isDraft(),
      cache: this.cache(),
      processApiPath: this.processApiPath(),
      resourceDefinitionId: this.resourceDefinitionId(),
      startParams: this.startParams(),
      fw: 'angular',
    })

    // Restore full-screen process after refresh — but only when the restored
    // process is NOT embedded in the UI flow template tree. When embedded,
    // initUiFlow has already hydrated stores + SSE; the template tree mounts
    // FlxProcessRendererWrapperComponent which short-circuits initProcess via
    // the populated processInstanceStore.
    const restoredEmbedded = getUiFlowRestoredProcessEmbedded()
    if (hadFullScreenProcess && restoredEmbedded === false) {
      this.processData.set({ processResourceId: '', processStartData: {} })
      this.showProcess.set(true)
    }
  }

  ngOnDestroy(): void {
    resetChatStores()
    updateUrlWithQueryParam('processUuid', null)
    resetUiFlowStores()
    resetNavigationStack()
  }

  loadProcess(
    processResourceId: string,
    processStartData: Record<string, unknown>,
    appId?: string,
    buildId?: string
  ): void {
    const currentViewId = getUrlQueryParamValue('viewId') ?? ''
    navigationStack.push({ viewId: currentViewId }, { processUuid: 'pending' })
    this.processData.set({ processResourceId, processStartData, appId, buildId })
    this.showProcess.set(true)
  }

  navigateToTemplateConfig({ flowxUuid: destinationViewId }: { flowxUuid: string }): void {
    const currentViewId = getUrlQueryParamValue('viewId')

    if (destinationViewId) {
      const { nextQueryParams, urlUpdateParams } = resolveRootNavigationQueryParams(
        currentViewId,
        destinationViewId,
        this.#activatedRoute.snapshot.queryParams
      )

      // Pre-compute the target URL so the stack entry captures the clean URL
      // (without stale processUuid) rather than the current window.location.href.
      // Push BEFORE URL changes so the previous history entry keeps its URL.
      const targetUrl = new URL(window.location.href)
      for (const [key, value] of Object.entries(urlUpdateParams)) {
        if (value == null) {
          targetUrl.searchParams.delete(key)
        } else {
          targetUrl.searchParams.set(key, value)
        }
      }
      navigationStack.push({ viewId: destinationViewId }, {}, targetUrl.toString())
      triggerDisplayEvent('navigation', destinationViewId)
      this.#router.navigate([], {
        relativeTo: this.#activatedRoute,
        queryParams: nextQueryParams,
        replaceUrl: true,
      })
      // The router.navigate above updates Angular's internal URL state via replaceState.
      // This second replaceState call (inside updateUrlWithQueryParams) patches in SDK-specific
      // query params (e.g. processUuid, viewId) and emits urlUpdate$ so that SDK subscriptions
      // (modalIdFromUrl$, etc.) see the final URL. The ordering is safe because skip(1) and
      // distinctUntilChanged on those subscriptions prevent spurious re-emissions.
      updateUrlWithQueryParams(urlUpdateParams)
    }
  }
}
